import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Download,
    Trash2,
    Mail,
    Phone,
    Calendar,
    MapPin,
    GraduationCap,
    Award,
    FileText,
    ExternalLink,
    Briefcase,
    LayoutGrid,
    List,
    X,
    Eye,
    RotateCcw
} from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { AdminLayout } from './components/AdminLayout';
import { AdminTopBar } from './components/AdminTopBar';
import { SearchAndFilters } from './components/SearchAndFilters';
import { BackLink } from './components/BackLink';
import { EmptyState } from './components/EmptyState';
import { Pagination } from './components/Pagination';
import { resumeStorage, downloadBase64File, StoredResume } from '../../utils/resumeStorage';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { resumesAPI } from '../../../services/api';
import { exportResumesToExcel } from '../../utils/excelExport';
import { useSocketEvent } from '../../contexts/SocketContext';

// Type alias for backward compatibility
type Resume = StoredResume;

export function AdminResumes() {
    const [resumes, setResumes] = useState<Resume[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
    const [showViewAll, setShowViewAll] = useState(false);
    const itemsPerPage = viewMode === 'grid' ? 6 : 5;
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        itemId: '',
        itemName: '',
        isDeleting: false
    });
    const [isLoading, setIsLoading] = useState(true);

    // Get current user from localStorage to check if super_admin
    const currentUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    const isSuperAdmin = currentUser?.role === 'super_admin';

    // Fetch resumes from MongoDB API
    const fetchResumes = async () => {
        try {
            setIsLoading(true);
            const response = await resumesAPI.getAll();
            
            // Map MongoDB resumes to match Resume interface
            const apiResumes = response.data.map((resume: any) => ({
                id: resume._id || resume.id,
                name: resume.name,
                email: resume.email,
                phone: resume.phone,
                location: resume.location || '',
                currentRole: resume.currentRole || '',
                experience: resume.experience || '',
                education: resume.education || '',
                summary: resume.summary || '',
                skills: resume.skills || [],
                resumeUrl: resume.resumeUrl || '',
                resumeFileName: resume.resumeFileName || '',
                resumeFileType: resume.resumeFileType || 'application/pdf',
                status: resume.status || 'new',
                submittedDate: resume.createdAt || resume.submittedDate || resume.uploadedDate || new Date().toISOString(),
                uploadedDate: resume.createdAt || resume.uploadedDate || resume.submittedDate || new Date().toISOString(),
                rating: resume.rating || 0,
                linkedIn: resume.linkedIn || resume.linkedinUrl || '',
                portfolio: resume.portfolio || resume.portfolioUrl || '',
                visibility: resume.visibility || 'visible',
            }));

            setResumes(apiResumes);
        } catch (error) {
            console.error('Error fetching resumes from API:', error);
            toast.error('Failed to load resumes from database');
            setResumes([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch on mount
    useEffect(() => {
        fetchResumes();
    }, []);

    // Real-time socket event handlers
    useSocketEvent('resume:created', useCallback((data: any) => {
        if (!data?.resume) return; // Guard against undefined data
        const resume = data.resume;
        const newResume: Resume = {
            id: resume._id || resume.id,
            name: resume.name,
            email: resume.email,
            phone: resume.phone,
            location: resume.location || '',
            currentRole: resume.currentRole || '',
            experience: resume.experience || '',
            education: resume.education || '',
            summary: resume.summary || '',
            skills: resume.skills || [],
            resumeUrl: resume.resumeUrl || '',
            resumeFileName: resume.resumeFileName || '',
            resumeFileType: resume.resumeFileType || 'application/pdf',
            status: resume.status || 'new',
            submittedDate: resume.createdAt || resume.submittedDate || new Date().toISOString(),
            uploadedDate: resume.createdAt || resume.uploadedDate || new Date().toISOString(),
            rating: resume.rating || 0,
            linkedIn: resume.linkedIn || resume.linkedinUrl || '',
            portfolio: resume.portfolio || resume.portfolioUrl || '',
            visibility: resume.visibility || 'visible',
        };
        setResumes(prev => [newResume, ...prev]);
        toast.info(`New resume submitted by ${resume.name}!`);
    }, []));

    useSocketEvent('resume:updated', useCallback((data: any) => {
        if (!data?.resume) return; // Guard against undefined data
        const resume = data.resume;
        setResumes(prev => prev.map(r => {
            if (r.id === (resume._id || resume.id)) {
                return {
                    ...r,
                    status: resume.status ?? r.status,
                    visibility: resume.visibility ?? r.visibility,
                    rating: resume.rating ?? r.rating,
                };
            }
            return r;
        }));
        toast.info('Resume updated');
    }, []));

    useSocketEvent('resume:deleted', useCallback((data: any) => {
        if (!data) return; // Guard against undefined data
        const deletedId = data.resume?._id || data.resume?.id || data.id;
        if (!deletedId) return;
        setResumes(prev => prev.filter(r => r.id !== deletedId));
        toast.info('Resume removed');
    }, []));

    const handleStatusChange = async (id: string, newStatus: Resume['status']) => {
        try {
            // Update in MongoDB via API
            await resumesAPI.update(id, { status: newStatus });
            
            // Update local state
            const updatedResumes = resumes.map(resume => 
                resume.id === id ? { ...resume, status: newStatus } : resume
            );
            setResumes(updatedResumes);
            
            if (selectedResume?.id === id) {
                setSelectedResume({ ...selectedResume, status: newStatus });
            }
            
            toast.success('Status updated');
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleSelectResume = (resume: Resume) => {
        setSelectedResume(resume);
        // Auto-mark as reviewed if it's new
        if (resume.status === 'new') {
            handleStatusChange(resume.id, 'reviewed');
        }
    };

    const handleDelete = (id: string, name: string) => {
        setDeleteModal({
            isOpen: true,
            itemId: id,
            itemName: name,
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));
        try {
            // Delete from MongoDB via API
            await resumesAPI.delete(deleteModal.itemId);
            
            if (isSuperAdmin) {
                // Super admin: item is permanently deleted, remove from list
                const updatedResumes = resumes.filter(r => r.id !== deleteModal.itemId);
                setResumes(updatedResumes);
            } else {
                // Regular admin: item is soft-deleted, update visibility
                const updatedResumes = resumes.map(r =>
                    r.id === deleteModal.itemId ? { ...r, visibility: 'hidden' as const } : r
                );
                setResumes(updatedResumes);
            }
            
            // Clear selection if deleted resume was selected
            if (selectedResume?.id === deleteModal.itemId) {
                setSelectedResume(null);
            }
            
            toast.success(isSuperAdmin ? 'Resume permanently deleted' : 'Resume deleted successfully');
            setDeleteModal({
                isOpen: false,
                itemId: '',
                itemName: '',
                isDeleting: false
            });
        } catch (error) {
            toast.error('Failed to delete resume');
            setDeleteModal(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleExport = () => {
        try {
            // Get the currently filtered resumes
            const dataToExport = filteredResumes;
            
            if (dataToExport.length === 0) {
                toast.error('No data to export');
                return;
            }

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const filterInfo = statusFilter !== 'all' || searchTerm ? '_filtered' : '_all';
            const filename = `resumes${filterInfo}_${timestamp}.xlsx`;

            // Export to Excel
            exportResumesToExcel(dataToExport, filename);
            
            toast.success(`Exported ${dataToExport.length} resume(s) successfully`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export data');
        }
    };

    const filteredResumes = resumes.filter(resume => {
        const matchesSearch = 
            resume.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            resume.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            resume.currentRole.toLowerCase().includes(searchTerm.toLowerCase()) ||
            resume.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesStatus = statusFilter === 'all' || resume.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredResumes.length / itemsPerPage);
    const paginatedResumes = filteredResumes.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const statusColors = {
        new: 'bg-primary/20 text-primary border-primary/30',
        reviewed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        contacted: 'bg-green-500/20 text-green-400 border-green-500/30',
        archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };

    const visibilityColors = {
        visible: '',
        hidden: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    const handleRestore = async (id: string) => {
        try {
            await resumesAPI.update(id, { visibility: 'visible' });
            setResumes(resumes.map(r =>
                r.id === id ? { ...r, visibility: 'visible' } : r
            ));
            if (selectedResume?.id === id) {
                setSelectedResume({ ...selectedResume, visibility: 'visible' });
            }
            toast.success('Resume restored successfully');
        } catch (error) {
            console.error('Error restoring resume:', error);
            toast.error('Failed to restore resume');
        }
    };

    return (
        <AdminLayout>
            <AdminTopBar
                title="Resume Submissions"
                description="Review and manage resume submissions"
                actionButton={{
                    label: 'Export',
                    icon: <Download className="w-4 h-4 mr-2" />,
                    onClick: handleExport
                }}
            />

            <div className="p-8 space-y-6">
                <SearchAndFilters
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder="Search by name, email, role, or skills..."
                    filters={[
                        {
                            value: statusFilter,
                            onChange: setStatusFilter,
                            options: [
                                { value: 'all', label: 'All Status' },
                                { value: 'new', label: 'New' },
                                { value: 'reviewed', label: 'Reviewed' },
                                { value: 'contacted', label: 'Contacted' },
                                { value: 'archived', label: 'Archived' }
                            ],
                            label: 'Status Filter'
                        }
                    ]}
                />

                {/* View Toggle */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-2 glass-strong rounded-lg border border-primary/30 p-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewMode('list')}
                            className={viewMode === 'list' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}
                        >
                            <List className="w-4 h-4 mr-2" />
                            List
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewMode('grid')}
                            className={viewMode === 'grid' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}
                        >
                            <LayoutGrid className="w-4 h-4 mr-2" />
                            Grid
                        </Button>
                    </div>
                </div>

                {/* Resume List and Detail View */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* List */}
                    <div className="glass-strong rounded-xl border border-primary/30 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-foreground">
                                Resumes ({isLoading ? '...' : filteredResumes.length})
                            </h3>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowViewAll(true)}
                                className="glass border-primary/30 text-primary hover:bg-primary/10"
                            >
                                <Eye className="w-4 h-4 mr-2" />
                                View All
                            </Button>
                        </div>
                        <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : 'space-y-2'}>
                            {isLoading ? (
                                // Loading skeleton
                                Array.from({ length: viewMode === 'grid' ? 6 : 5 }).map((_, index) => (
                                    <div key={index} className={`p-4 rounded-lg glass ${viewMode === 'grid' ? 'h-full' : ''}`}>
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="h-4 w-24 bg-muted/20 animate-pulse rounded mb-1"></div>
                                                <div className="h-3 w-20 bg-muted/20 animate-pulse rounded"></div>
                                            </div>
                                            <div className="flex gap-2 ml-2">
                                                <div className="h-5 w-12 bg-muted/20 animate-pulse rounded"></div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 mb-2">
                                            <div className="h-3 w-3 bg-muted/20 animate-pulse rounded"></div>
                                            <div className="h-3 w-16 bg-muted/20 animate-pulse rounded"></div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="h-3 w-12 bg-muted/20 animate-pulse rounded"></div>
                                            <div className="h-3 w-16 bg-muted/20 animate-pulse rounded"></div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <>
                                    {paginatedResumes.map((resume) => (
                                <motion.div
                                    key={resume.id}
                                    whileHover={{ x: viewMode === 'list' ? 4 : 0, scale: viewMode === 'grid' ? 1.02 : 1 }}
                                    onClick={() => handleSelectResume(resume)}
                                    className={`p-4 rounded-lg glass cursor-pointer transition-all ${
                                        selectedResume?.id === resume.id
                                            ? 'bg-primary/10 border border-primary/30'
                                            : 'hover:bg-primary/5'
                                    } ${viewMode === 'grid' ? 'h-full' : ''}`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-foreground truncate">{resume.name}</h4>
                                            <p className="text-sm text-muted-foreground truncate">
                                                {resume.currentRole}
                                            </p>
                                        </div>
                                        <div className="flex gap-1 ml-2 flex-wrap">
                                            <Badge className={`text-xs ${statusColors[resume.status]}`}>
                                                {resume.status}
                                            </Badge>
                                            {resume.visibility === 'hidden' && (
                                                <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                    Deleted
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {resume.location}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">{resume.experience} exp</span>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(resume.uploadedDate || resume.submittedDate || new Date().toISOString()).toLocaleDateString()}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                                </>
                            )}
                            {!isLoading && filteredResumes.length === 0 && (
                                <EmptyState
                                    icon={FileText}
                                    title="No resumes found"
                                    description="Try adjusting your search or filters"
                                />
                            )}
                        </div>
                        {!isLoading && filteredResumes.length > 0 && (
                            <div className="mt-4">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={filteredResumes.length}
                                />
                            </div>
                        )}
                    </div>

                    {/* Detail View */}
                    <div className="glass-strong rounded-xl border border-primary/30 p-6">
                        {selectedResume ? (
                            <div className="space-y-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="text-foreground mb-1">{selectedResume.name}</h3>
                                        <p className="text-sm text-muted-foreground mb-2">{selectedResume.currentRole}</p>
                                        <div className="flex gap-2 flex-wrap">
                                            <Badge className={`text-xs ${statusColors[selectedResume.status]}`}>
                                                {selectedResume.status}
                                            </Badge>
                                            {selectedResume.visibility === 'hidden' && (
                                                <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                    Deleted
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        {isSuperAdmin && selectedResume.visibility === 'hidden' && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRestore(selectedResume.id)}
                                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                                title="Restore Resume"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(selectedResume.id, selectedResume.name)}
                                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                            title={isSuperAdmin ? "Permanent Delete" : "Delete"}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        <a
                                            href={`mailto:${selectedResume.email}`}
                                            className="text-primary hover:text-primary/80"
                                        >
                                            {selectedResume.email}
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Phone className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">{selectedResume.phone}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <MapPin className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">{selectedResume.location}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">{selectedResume.experience} experience</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">
                                            Submitted: {new Date(selectedResume.uploadedDate || selectedResume.submittedDate || new Date().toISOString()).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-foreground mb-2 flex items-center gap-2">
                                        <GraduationCap className="w-4 h-4" />
                                        Education
                                    </h4>
                                    <p className="text-muted-foreground">{selectedResume.education}</p>
                                </div>

                                <div>
                                    <h4 className="text-foreground mb-3">Summary</h4>
                                    <div className="space-y-2 text-muted-foreground">
                                        <p>
                                            <span className="text-foreground">Applied for:</span> {selectedResume.currentRole || 'Not specified'}
                                        </p>
                                        <p>
                                            <span className="text-foreground">Experience:</span> {selectedResume.experience}
                                        </p>
                                        {selectedResume.expectedSalary && (
                                            <p>
                                                <span className="text-foreground">Expected Salary:</span> {selectedResume.expectedSalary}
                                            </p>
                                        )}
                                        {selectedResume.noticePeriod && (
                                            <p>
                                                <span className="text-foreground">Notice Period:</span> {selectedResume.noticePeriod}
                                            </p>
                                        )}
                                        <p>
                                            <span className="text-foreground">Skills:</span> {selectedResume.skills.join(', ')}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-foreground mb-2">Additional Skills</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedResume.skills.map((skill, index) => (
                                            <Badge key={index} className="bg-primary/20 text-primary border-primary/30">
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {selectedResume.certifications && selectedResume.certifications.length > 0 && (
                                    <div>
                                        <h4 className="text-foreground mb-2 flex items-center gap-2">
                                            <Award className="w-4 h-4" />
                                            Certifications
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedResume.certifications.map((cert, index) => (
                                                <Badge key={index} className="bg-green-500/20 text-green-400 border-green-500/30">
                                                    {cert}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-2 pt-4 border-t border-border/30 flex-wrap">
                                    <Button
                                        onClick={() => downloadBase64File(selectedResume.resumeUrl, selectedResume.resumeFileName || `${selectedResume.name}_resume.pdf`)}
                                        variant="outline"
                                        className="glass border-border/30"
                                    >
                                        <FileText className="w-4 h-4 mr-2" />
                                        View Resume
                                    </Button>
                                    {selectedResume.linkedIn && (
                                        <Button
                                            onClick={() => window.open(`https://${selectedResume.linkedIn}`, '_blank')}
                                            variant="outline"
                                            className="glass border-border/30"
                                        >
                                            <ExternalLink className="w-4 h-4 mr-2" />
                                            LinkedIn
                                        </Button>
                                    )}
                                    {selectedResume.portfolio && (
                                        <Button
                                            onClick={() => window.open(`https://${selectedResume.portfolio}`, '_blank')}
                                            variant="outline"
                                            className="glass border-border/30"
                                        >
                                            <ExternalLink className="w-4 h-4 mr-2" />
                                            Portfolio
                                        </Button>
                                    )}
                                    <select
                                        value={selectedResume.status}
                                        onChange={(e) => handleStatusChange(selectedResume.id, e.target.value as Resume['status'])}
                                        className="flex-1 min-w-[120px] px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background"
                                    >
                                        <option value="new">New</option>
                                        <option value="reviewed">Reviewed</option>
                                        <option value="contacted">Contacted</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                                Select a resume to view details
                            </div>
                        )}
                    </div>
                </div>

                <BackLink to="/admin" label="Back to Dashboard" />
            </div>

            {/* View All Modal */}
            <AnimatePresence>
                {showViewAll && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowViewAll(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-strong rounded-2xl border border-primary/30 p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-foreground">
                                    All Resumes ({filteredResumes.length})
                                </h2>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowViewAll(false)}
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            <div className="overflow-y-auto flex-1">
                                <div className="space-y-2">
                                    {filteredResumes.slice(0, 50).map((resume) => (
                                        <motion.div
                                            key={resume.id}
                                            whileHover={{ x: 4 }}
                                            onClick={() => {
                                                handleSelectResume(resume);
                                                setShowViewAll(false);
                                            }}
                                            className="p-4 rounded-lg glass cursor-pointer hover:bg-primary/5 transition-all"
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-foreground truncate">{resume.name}</h4>
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {resume.currentRole}
                                                    </p>
                                                </div>
                                                <div className="flex gap-1 ml-2 flex-wrap">
                                                    <Badge className={`text-xs ${statusColors[resume.status]}`}>
                                                        {resume.status}
                                                    </Badge>
                                                    {resume.visibility === 'hidden' && (
                                                        <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                            Deleted
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {resume.location}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground">{resume.experience} exp</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {new Date(resume.uploadedDate || resume.submittedDate || new Date().toISOString()).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => !deleteModal.isDeleting && setDeleteModal({ isOpen: false, itemId: '', itemName: '', isDeleting: false })}
                onConfirm={confirmDelete}
                title={isSuperAdmin ? "Permanently Delete Resume" : "Delete Resume"}
                message={isSuperAdmin ? "Are you sure you want to permanently delete this resume?" : "Are you sure you want to delete this resume?"}
                itemName={deleteModal.itemName}
                isDeleting={deleteModal.isDeleting}
                isPermanent={isSuperAdmin}
            />
        </AdminLayout>
    );
}
